import { useState } from "react";
import { isIOS } from "../lib/platform";

// The real thing, on a real phone, doing the exact taps.
//
// Written instructions fail the people this most needs to reach. "Tap the share
// button, scroll down, tap Add to Home Screen" is four unfamiliar words and a
// menu somebody has never opened, and the step people actually miss is the last
// one, that the icon IS the app. A recording of a thumb doing it removes the
// guesswork that text cannot.
//
// Loaded per platform and per step on purpose. These are a couple of megabytes
// each and a member is often on cellular in the middle of joining, so an iPhone
// never downloads the Android pair, and the notifications clip is not fetched
// until somebody reaches that step.

export type Which = "install" | "notifications";

export const SOURCES: Record<Which, { ios: string; android: string; alt: string }> = {
  install: {
    ios: "/onboarding/ios-install.gif",
    android: "/onboarding/android-install.gif",
    alt: "Tapping Share, then Add to Home Screen, then Add. The new icon appears on the home screen.",
  },
  notifications: {
    ios: "/onboarding/ios-notifications.gif",
    android: "/onboarding/android-notifications.gif",
    alt: "Opening the app from the home screen icon and tapping Allow when the phone asks about notifications.",
  },
};

// Which file a given platform gets. Pulled out so the mapping can be tested
// without emulating a user agent, which is the part most likely to be silently
// wrong: an iPhone shown Android's menus is worse than no pictures at all,
// because it looks authoritative and describes a phone the person is not holding.
export function installMediaSrc(which: Which, ios: boolean): string {
  return ios ? SOURCES[which].ios : SOURCES[which].android;
}

export function InstallMedia({ which }: { which: Which }) {
  const [failed, setFailed] = useState(false);
  const src = installMediaSrc(which, isIOS());

  // If the file is missing the written steps beside it still carry the whole
  // instruction, so a broken image icon is strictly worse than nothing.
  if (failed) return null;

  return (
    <figure className="install-media">
      <img
        src={src}
        alt={SOURCES[which].alt}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
      <figcaption className="muted">
        {which === "install"
          ? "What it looks like on your phone."
          : "Then open it from the icon and tap Allow."}
      </figcaption>
    </figure>
  );
}
